const elementName = 'aceii-page-anchor-nav';
const activeDocumentClass = 'aceii-page-anchor-nav-active';

if (
  typeof window !== 'undefined'
  && typeof HTMLElement !== 'undefined'
  && !window.customElements.get(elementName)
) {
  class AceiiPageAnchorNav extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.sectionWrapper = this.closest('.shopify-section');
      this.mobileMenu = this.querySelector('[data-mobile-menu]');
      this.links = [...this.querySelectorAll('[data-page-anchor-target]')];
      this.pageSections = [...document.querySelectorAll('#MainContent > .shopify-section')];
      this.entries = this.buildEntries();

      if (!this.entries.length) {
        if (this.sectionWrapper) this.sectionWrapper.hidden = true;
        return;
      }

      this.currentTargetId = null;
      this.frame = null;
      this.visible = false;

      this.links.forEach((link) => {
        link.addEventListener('click', () => {
          this.setActiveTarget(link.dataset.pageAnchorTarget);
          if (this.mobileMenu) this.mobileMenu.open = false;
        }, { signal });
      });

      document.addEventListener('click', (event) => {
        if (this.mobileMenu?.open && !this.mobileMenu.contains(event.target)) {
          this.mobileMenu.open = false;
        }
      }, { signal });

      this.handleScroll = this.handleScroll.bind(this);
      window.addEventListener('scroll', this.handleScroll, { passive: true, signal });
      window.addEventListener('resize', this.handleScroll, { passive: true, signal });

      this.resizeObserver = new ResizeObserver(() => this.updateMetrics());
      this.resizeObserver.observe(this);

      this.observeHeroTrigger();
      this.updateMetrics();
      this.updateActiveLink();
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.frame);
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.abortController?.abort();
      this.abortController = null;
      document.documentElement.classList.remove(activeDocumentClass);
      document.documentElement.style.removeProperty('--aceii-page-anchor-nav-height');
    }

    buildEntries() {
      const entries = [];
      const seenTargets = new Set();

      this.links.forEach((link) => {
        const targetId = link.dataset.pageAnchorTarget;
        const target = this.pageSections.find((section) => section.id.endsWith(`__${targetId}`));
        const item = link.closest('[data-page-anchor-item]');

        if (item) item.hidden = !target;
        if (!target) return;

        link.setAttribute('href', `#${target.id}`);
        target.style.scrollMarginTop = 'calc(var(--aceii-page-anchor-nav-height, 68px) + 10px)';

        if (!seenTargets.has(targetId)) {
          seenTargets.add(targetId);
          entries.push({ targetId, target });
        }
      });

      return entries;
    }

    observeHeroTrigger() {
      const triggerSectionId = this.dataset.triggerSection;
      const heroSection = this.pageSections.find(
        (section) => section.id.endsWith(`__${triggerSectionId}`),
      );
      const trigger = heroSection?.querySelector('.hdt-group-button-block') || heroSection;

      if (!trigger) {
        this.setVisible(true);
        return;
      }

      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        const hasPassedTrigger = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        this.setVisible(hasPassedTrigger);
      }, { threshold: 0 });
      this.visibilityObserver.observe(trigger);
    }

    updateMetrics() {
      document.documentElement.style.setProperty(
        '--aceii-page-anchor-nav-height',
        `${this.getBoundingClientRect().height}px`,
      );
    }

    setVisible(visible) {
      if (visible === this.visible) return;

      this.visible = visible;
      this.classList.toggle('is-visible', visible);
      this.toggleAttribute('inert', !visible);
      this.setAttribute('aria-hidden', String(!visible));
      document.documentElement.classList.toggle(activeDocumentClass, visible);

      if (!visible && this.mobileMenu) this.mobileMenu.open = false;
      if (visible) this.updateActiveLink();
    }

    handleScroll() {
      if (!this.visible || this.frame) return;

      this.frame = window.requestAnimationFrame(() => {
        this.updateActiveLink();
        this.frame = null;
      });
    }

    updateActiveLink() {
      const activationLine = this.getBoundingClientRect().height + 12;
      let activeEntry = this.entries[0];

      for (const entry of this.entries) {
        if (entry.target.getBoundingClientRect().top <= activationLine) {
          activeEntry = entry;
        } else {
          break;
        }
      }

      this.setActiveTarget(activeEntry?.targetId);
    }

    setActiveTarget(targetId) {
      if (!targetId || targetId === this.currentTargetId) return;

      this.links.forEach((link) => {
        const active = link.dataset.pageAnchorTarget === targetId;
        link.classList.toggle('is-active', active);
        if (active) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      this.currentTargetId = targetId;
    }
  }

  window.customElements.define(elementName, AceiiPageAnchorNav);
}
